import React, { useState, useEffect, forwardRef } from 'react';
import isNil from 'lodash/isNil';
import cn from 'classnames';
import { Error, Hint } from '@progress/kendo-react-labels';
import BaseInput from '../BaseInput';
// Helpers
import { dateFormatter } from '../../../constants/Formatting';

const numericRegex = /^(-?\d+)$/;

/**
 * @typedef {Omit<React.ComponentPropsWithoutRef<BaseInput>, 'value'>} BaseInputProps
 *
 * @typedef {Object} AdditionalDateInputProps
 * @property {string | number} [value] Represents the date value.
 * @property {boolean} [visited] Indicates if the input is visited (focused and blurred).
 * @property {boolean} [autoFillYear={true}] Indicates if the value's year can be auto filled with the current year if another one is not set. True by default.
 * @property {boolean} [allowNumeric={false}] Indicates if a number can be used as a value. If true, the value represents a number of days from today.
 *
 * @param {BaseInputProps & AdditionalDateInputProps} props
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement}
 */
const CustomDateInput = ({
  id,
  onChange,
  value,
  onBlur,
  placeholder,
  visited,
  validationMessage,
  autoFillYear = true,
  allowNumeric = false,
  className,
  dataTestId,
  ...props
}, ref) => {
  const [textDate, setTextDate] = useState();

  const handleBlur = (e) => {
    let dateValue = value;
    if (e.target.value) {
      if (allowNumeric && numericRegex.test(e.target.value.toString())) {
        setTextDate(e.target.value);
        onChange({ value: e.target.value });
        return;
      }

      const formatted = dateFormatter(e.target.value, autoFillYear);

      setTextDate(formatted);
      const newValue = new Date(formatted);
      const isDateObject = dateValue instanceof Date;
      if (dateValue && !isDateObject) {
        dateValue = new Date(value);
      }

      // Check if any change have happened to avoid firing onChange for the same value
      if (newValue?.toISOString() !== dateValue?.toISOString()) {
        onChange({ value: newValue });
      }
    } else {
      setTextDate('');

      // Check if Date value is not null already to avoid firing onChange for the same value
      if (dateValue) {
        onChange({ value: null });
      }
    }

    if (onBlur) {
      onBlur(e);
    }
  };

  useEffect(() => {
    if (allowNumeric && !isNil(value) && numericRegex.test(value.toString())) {
      setTextDate(value.toString());
    } else if (value) {
      setTextDate(dateFormatter(value.toString()));
    } else {
      setTextDate('');
    }
  }, [value, allowNumeric]);

  const showValidationMessage = visited && validationMessage;
  const errorId = showValidationMessage ? `${id}_error` : '';

  const handleChange = ({ target: { value } }) => {
    setTextDate(value);
  };

  const inputClassName = cn('k-input-inner', { [className]: !!className });

  // Show hint for numeric values
  const showNumericHint = (
    allowNumeric && !isNil(textDate) && textDate.length !== 0 && numericRegex.test(textDate.toString()));
  const hintValue = showNumericHint ? Number(textDate) : null;

  return (
    <>
      <BaseInput
        ref={ref}
        placeholder={placeholder ?? 'mm/dd/yyyy or days'}
        {...props}
        className={inputClassName}
        onBlur={handleBlur}
        value={textDate}
        onChange={handleChange}
        data-testid={dataTestId}
      />
      {showNumericHint && (
        <Hint>
          {hintValue === 0 && 'Today'}
          {hintValue === -1 && 'Yesterday'}
          {hintValue === 1 && 'Tomorrow'}
          {hintValue > 1 && 'Days from now'}
          {hintValue < -1 && 'Days ago'}
        </Hint>
      )}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </>
  );
};

/**
 * A custom date input component.
 *
 * @param {BaseInputProps & AdditionalDateInputProps} props - Component props. Extends BaseInput props.
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement} Custom date input component.
 */
const ForwardCustomDateInput = forwardRef(CustomDateInput);

export default ForwardCustomDateInput;
